/**
 * BY MANAY — draggable launcher + FAQ chat panel (estilo Ohlunara).
 */
const LAUNCHER_STORAGE_KEY = 'bymanay-live-chat-position-v1';
const PANEL_STORAGE_KEY = 'bymanay-live-chat-panel-position-v1';
const DRAG_THRESHOLD_PX = 8;

/** @returns {{ minLeft: number; minTop: number; maxLeft: number; maxTop: number; width: number; height: number }} */
function viewportBounds() {
  const vv = window.visualViewport;
  const width = vv?.width ?? window.innerWidth;
  const height = vv?.height ?? window.innerHeight;
  const offsetLeft = vv?.offsetLeft ?? 0;
  const offsetTop = vv?.offsetTop ?? 0;
  return {
    width,
    height,
    minLeft: offsetLeft,
    minTop: offsetTop,
    maxLeft: offsetLeft + width,
    maxTop: offsetTop + height,
  };
}

/** @param {HTMLElement} el @param {number} [pad] */
function clampFixedElement(el, pad = 0) {
  const rect = el.getBoundingClientRect();
  const w = rect.width || el.offsetWidth;
  const h = rect.height || el.offsetHeight;
  let left = parseFloat(el.style.left);
  let top = parseFloat(el.style.top);
  if (Number.isNaN(left) || Number.isNaN(top)) {
    left = rect.left;
    top = rect.top;
  }
  const vp = viewportBounds();
  const minLeft = vp.minLeft + pad;
  const minTop = vp.minTop + pad;
  const maxLeft = Math.max(minLeft, vp.maxLeft - w - pad);
  const maxTop = Math.max(minTop, vp.maxTop - h - pad);
  left = Math.min(Math.max(minLeft, left), maxLeft);
  top = Math.min(Math.max(minTop, top), maxTop);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  el.style.right = 'auto';
  el.style.bottom = 'auto';
}

/** @returns {(Document | ShadowRoot)[]} */
function allRoots() {
  /** @type {(Document | ShadowRoot)[]} */
  const roots = [document];
  const walk = (/** @type {Document | ShadowRoot} */ root) => {
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        roots.push(el.shadowRoot);
        walk(el.shadowRoot);
      }
    });
  };
  walk(document);
  return roots;
}

/** @returns {{ el: HTMLButtonElement; root: Document | ShadowRoot } | null} */
function findInboxActivator() {
  for (const root of allRoots()) {
    const el = root.querySelector('button.activator');
    if (el instanceof HTMLButtonElement) return { el, root };
  }
  return null;
}

const HIDE_ACTIVATOR_CSS =
  '.activator{opacity:0!important;visibility:hidden!important;pointer-events:none!important;width:0!important;height:0!important;overflow:hidden!important;margin:0!important;padding:0!important;border:0!important}';

function hideDefaultInboxButton() {
  const found = findInboxActivator();
  if (!found || found.root.querySelector('style[data-bymanay-hide-inbox]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-bymanay-hide-inbox', '1');
  style.textContent = HIDE_ACTIVATOR_CSS;
  found.root.appendChild(style);
}

function openInboxChat() {
  hideDefaultInboxButton();
  const found = findInboxActivator();
  if (found?.el) {
    found.el.click();
    return true;
  }
  return false;
}

/** @returns {Record<string, { title?: string; text: string; action?: string; tab?: string }> | null} */
function readFaqConfig() {
  const el = document.getElementById('BymanayLiveChatFaqData');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/** @param {string} text */
function normalizeText(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
}

/** @param {string} input @param {Record<string, { keys?: string[]; topic: string }>} intents */
function matchTopic(input, intents) {
  const q = normalizeText(input);
  if (!q) return null;
  for (const intent of intents) {
    if (intent.keys?.some((k) => q.includes(normalizeText(k)))) return intent.topic;
  }
  return null;
}

/** @param {string} message */
function inferSizeGuideTab(message) {
  const q = normalizeText(message);
  if (/calzado|bota|zapato|tacon|numero|eu 3|pie/.test(q)) return 'calzado';
  if (/vestido/.test(q)) return 'vestidos';
  if (/chaqueta|abrigo|gabardina|prenda exterior/.test(q)) return 'chaquetas';
  if (/top|blusa|body|conjunto/.test(q)) return 'tops';
  return 'chaquetas';
}

function openSizeGuide(tab) {
  if (window.BymanaySizeGuide?.open) {
    window.BymanaySizeGuide.open({ tab });
    return true;
  }
  const trigger = document.querySelector(`[data-bymanay-open-size-guide][data-bymanay-size-guide-tab="${tab}"]`) ||
    document.querySelector('[data-bymanay-open-size-guide]');
  if (trigger instanceof HTMLElement) {
    trigger.click();
    return true;
  }
  return false;
}

class BymanayLiveChatPanel extends HTMLElement {
  /** @type {Record<string, { title?: string; text: string; action?: string; tab?: string }>} */
  #topics = {};
  /** @type {{ keys?: string[]; topic: string }[]} */
  #intents = [];

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.#loadConfig();
    this.#bind();
    this.#restorePosition();
  }

  #loadConfig() {
    const cfg = readFaqConfig();
    this.#topics = cfg?.topics || {};
    this.#intents = cfg?.intents || [];
    const greeting = this.#topics.greeting?.text;
    const bubble = this.querySelector('[data-bymanay-chat-greeting]');
    if (bubble && greeting) bubble.textContent = greeting;
  }

  #bind() {
    const closeBtn = this.querySelector('[data-bymanay-chat-close]');
    closeBtn?.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
    });
    closeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    window.addEventListener('keydown', this.#onKeyDown);
    this.querySelector('[data-bymanay-chat-form]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = this.querySelector('[data-bymanay-chat-input]');
      if (!(input instanceof HTMLInputElement)) return;
      const value = input.value.trim();
      if (!value) return;
      this.#appendUser(value);
      this.#reply(value);
      input.value = '';
    });
    this.querySelectorAll('[data-bymanay-chat-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const topic = chip.getAttribute('data-bymanay-chat-chip');
        if (!topic) return;
        const label = chip.textContent?.trim() || topic;
        this.#appendUser(label);
        this.#replyTopic(topic, label);
      });
    });
    this.#bindPanelDrag();
  }

  #bindPanelDrag() {
    const handle = this.querySelector('[data-bymanay-chat-drag-handle]');
    if (!(handle instanceof HTMLElement)) return;
    let pointerId = null;
    let moved = 0;
    let startX = 0;
    let startY = 0;
    let originLeft = 0;
    let originTop = 0;

    let draggingPanel = false;

    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (event.target.closest('[data-bymanay-chat-close]')) return;
      pointerId = event.pointerId;
      moved = 0;
      draggingPanel = false;
      const left = parseFloat(this.style.left) || this.getBoundingClientRect().left;
      const top = parseFloat(this.style.top) || this.getBoundingClientRect().top;
      this.style.left = `${left}px`;
      this.style.top = `${top}px`;
      this.style.right = 'auto';
      this.style.bottom = 'auto';
      startX = event.clientX;
      startY = event.clientY;
      originLeft = left;
      originTop = top;
    });

    handle.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      moved = Math.max(moved, Math.hypot(dx, dy));
      if (!draggingPanel && moved > DRAG_THRESHOLD_PX) {
        draggingPanel = true;
        try {
          handle.setPointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      }
      if (!draggingPanel) return;
      if (event.cancelable) event.preventDefault();
      this.style.left = `${originLeft + dx}px`;
      this.style.top = `${originTop + dy}px`;
      this.#clampToViewport();
    });

    const end = (event) => {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      if (draggingPanel && moved > DRAG_THRESHOLD_PX) this.#savePosition();
      draggingPanel = false;
      try {
        handle.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  #clampToViewport() {
    clampFixedElement(this, 8);
  }

  #savePosition() {
    try {
      localStorage.setItem(
        PANEL_STORAGE_KEY,
        JSON.stringify({
          left: parseFloat(this.style.left) || 0,
          top: parseFloat(this.style.top) || 0,
        })
      );
    } catch {
      /* ignore */
    }
  }

  #restorePosition() {
    try {
      const raw = localStorage.getItem(PANEL_STORAGE_KEY);
      if (!raw) return;
      const { left, top } = JSON.parse(raw);
      if (typeof left === 'number' && typeof top === 'number') {
        this.style.left = `${left}px`;
        this.style.top = `${top}px`;
        this.style.right = 'auto';
        this.style.bottom = 'auto';
      }
    } catch {
      /* ignore */
    }
  }

  /** @param {HTMLElement | null} launcher */
  open(launcher) {
    this.hidden = false;
    this.dataset.open = 'true';
    if (launcher) this.#positionNearLauncher(launcher);
    this.#clampToViewport();
    this.querySelector('[data-bymanay-chat-input]')?.focus({ preventScroll: true });
  }

  close() {
    this.setAttribute('hidden', '');
    this.hidden = true;
    delete this.dataset.open;
    document.querySelector('bymanay-live-chat-stack')?.removeAttribute('hidden');
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || this.hidden) return;
    event.preventDefault();
    this.close();
  };

  /** @param {HTMLElement} anchor */
  #positionNearLauncher(anchor) {
    const lr = anchor.getBoundingClientRect();
    const panelW = this.offsetWidth || 360;
    const panelH = this.offsetHeight || 480;
    let left = lr.left - panelW - 12;
    if (left < 8) left = Math.max(8, lr.left - panelW + 40);
    let top = lr.top - panelH + lr.height;
    if (top < 8) top = 8;
    if (window.innerWidth < 520) {
      left = 8;
      top = Math.max(8, window.innerHeight - panelH - 16);
    }
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;
    this.style.right = 'auto';
    this.style.bottom = 'auto';
  }

  /** @param {string} text */
  #appendUser(text) {
    const log = this.querySelector('[data-bymanay-chat-log]');
    if (!log) return;
    const row = document.createElement('div');
    row.className = 'bymanay-live-chat-panel__msg bymanay-live-chat-panel__msg--user';
    row.textContent = text;
    log.appendChild(row);
    log.scrollTop = log.scrollHeight;
  }

  /** @param {string} text @param {{ action?: string; tab?: string }} [meta] */
  #appendBot(text, meta = {}) {
    const log = this.querySelector('[data-bymanay-chat-log]');
    if (!log) return;
    const row = document.createElement('div');
    row.className = 'bymanay-live-chat-panel__msg bymanay-live-chat-panel__msg--bot';
    row.textContent = text;
    log.appendChild(row);
    if (meta.action === 'size-guide') {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bymanay-live-chat-panel__link-btn';
      btn.textContent = 'Ver guía de tallas';
      btn.addEventListener('click', () => openSizeGuide(meta.tab || 'chaquetas'));
      log.appendChild(btn);
    }
    if (meta.action === 'human') {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bymanay-live-chat-panel__link-btn';
      btn.textContent = 'Hablar con el equipo';
      btn.addEventListener('click', () => openInboxChat());
      log.appendChild(btn);
    }
    log.scrollTop = log.scrollHeight;
  }

  /** @param {string} topic @param {string} [label] */
  #replyTopic(topic, label = '') {
    const entry = this.#topics[topic];
    if (!entry) {
      this.#reply(label || topic);
      return;
    }
    this.#appendBot(entry.text, { action: entry.action, tab: entry.tab || inferSizeGuideTab(label) });
    if (entry.action === 'size-guide') {
      openSizeGuide(entry.tab || inferSizeGuideTab(label));
    }
  }

  /** @param {string} input */
  #reply(input) {
    const topic = matchTopic(input, this.#intents) || matchTopic(input, [
      { keys: ['hola', 'buenas', 'ayuda'], topic: 'greeting' },
    ]);
    if (topic && this.#topics[topic]) {
      const tab = topic === 'tallas' ? inferSizeGuideTab(input) : undefined;
      this.#appendBot(this.#topics[topic].text, {
        action: this.#topics[topic].action,
        tab: tab || this.#topics[topic].tab,
      });
      if (this.#topics[topic].action === 'size-guide') openSizeGuide(tab || 'chaquetas');
      return;
    }
    const pageFaq = this.#matchPageFaq(input);
    if (pageFaq) {
      this.#appendBot(pageFaq);
      return;
    }
    this.#appendBot(this.#topics.fallback?.text || 'Gracias por tu mensaje. Si necesitas ayuda personalizada, escríbenos desde la página de contacto.', {
      action: 'human',
    });
  }

  /** @param {string} input */
  #matchPageFaq(input) {
    const q = normalizeText(input);
    const items = document.querySelectorAll('#faq .abianca-faq__item');
    for (const item of items) {
      const question = item.querySelector('.abianca-faq__question span')?.textContent || '';
      const answer = item.querySelector('.abianca-faq__answer')?.textContent?.trim() || '';
      const nq = normalizeText(question);
      if (!answer || !nq) continue;
      if (q.includes(nq.slice(0, 12)) || nq.split(' ').some((w) => w.length > 4 && q.includes(w))) {
        return answer;
      }
    }
    return null;
  }
}

class BymanayLiveChatStack extends HTMLElement {
  #pointerId = null;
  /** @type {boolean} */
  #armed = false;
  #dragging = false;
  #startX = 0;
  #startY = 0;
  #originLeft = 0;
  #originTop = 0;
  #moved = 0;
  /** @type {HTMLElement | null} */
  #pendingSocialTarget = null;
  /** @type {((event: TouchEvent) => void) | null} */
  #blockTouchScroll = null;

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    document.body.classList.add('bymanay-has-live-chat-launcher');
    this.#restorePosition();
    this.#bind();
    this.hidden = false;
    hideDefaultInboxButton();
    window.setInterval(hideDefaultInboxButton, 1200);
  }

  getDragDistance() {
    return this.#moved;
  }

  #bind() {
    this.addEventListener('pointerdown', this.#onPointerDown, true);
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
    window.addEventListener('resize', this.#clampToViewport);
    window.visualViewport?.addEventListener('resize', this.#clampToViewport);
    window.visualViewport?.addEventListener('scroll', this.#clampToViewport);

    this.querySelectorAll('.bymanay-live-chat-stack__social').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        if (this.getDragDistance() > DRAG_THRESHOLD_PX || this.#dragging) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        this.#openSocial(btn);
      });
    });
  }

  /** @param {Element | null} el */
  #openSocial(el) {
    const href = el?.getAttribute('data-social-url') || el?.getAttribute('href');
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  #restorePosition() {
    try {
      const raw = localStorage.getItem(LAUNCHER_STORAGE_KEY);
      if (!raw) {
        this.#setDefaultPosition();
        return;
      }
      const { left, top } = JSON.parse(raw);
      if (typeof left === 'number' && typeof top === 'number') {
        this.style.left = `${left}px`;
        this.style.top = `${top}px`;
        this.style.right = 'auto';
        this.style.bottom = 'auto';
        this.#clampToViewport();
        return;
      }
    } catch {
      /* ignore */
    }
    this.#setDefaultPosition();
  }

  #setDefaultPosition() {
    const vp = viewportBounds();
    const width = this.offsetWidth || 132;
    const height = this.offsetHeight || 168;
    const left = Math.max(vp.minLeft + 12, vp.maxLeft - width - 8);
    const top = Math.max(vp.minTop + 72, vp.minTop + Math.round(vp.height * 0.48 - height / 2));
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;
    this.style.right = 'auto';
    this.style.bottom = 'auto';
  }

  #savePosition() {
    try {
      localStorage.setItem(
        LAUNCHER_STORAGE_KEY,
        JSON.stringify({
          left: parseFloat(this.style.left) || 0,
          top: parseFloat(this.style.top) || 0,
        })
      );
    } catch {
      /* ignore */
    }
  }

  #clampToViewport = () => {
    clampFixedElement(this, 0);
  };

  #startTouchScrollBlock() {
    if (this.#blockTouchScroll) return;
    this.#blockTouchScroll = (event) => {
      if (this.#dragging) event.preventDefault();
    };
    document.addEventListener('touchmove', this.#blockTouchScroll, { passive: false });
  }

  #stopTouchScrollBlock() {
    if (!this.#blockTouchScroll) return;
    document.removeEventListener('touchmove', this.#blockTouchScroll);
    this.#blockTouchScroll = null;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    this.#pointerId = event.pointerId;
    this.#armed = true;
    this.#dragging = false;
    this.#moved = 0;
    const social = event.target.closest('.bymanay-live-chat-stack__social');
    this.#pendingSocialTarget = social instanceof HTMLElement ? social : null;
    if (this.#pendingSocialTarget && event.cancelable) {
      event.preventDefault();
    }
    const left = parseFloat(this.style.left) || this.getBoundingClientRect().left;
    const top = parseFloat(this.style.top) || this.getBoundingClientRect().top;
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;
    this.style.right = 'auto';
    this.style.bottom = 'auto';
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#originLeft = left;
    this.#originTop = top;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    if (!this.#armed && !this.#dragging) return;
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;
    this.#moved = Math.max(this.#moved, Math.hypot(dx, dy));
    if (!this.#dragging && this.#moved > DRAG_THRESHOLD_PX) {
      this.#dragging = true;
      this.#pendingSocialTarget = null;
      this.dataset.dragging = 'true';
      this.#startTouchScrollBlock();
      try {
        this.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (!this.#dragging) return;
    if (event.cancelable) event.preventDefault();
    this.style.left = `${this.#originLeft + dx}px`;
    this.style.top = `${this.#originTop + dy}px`;
    this.#clampToViewport();
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    const openSocial =
      this.#pendingSocialTarget && !this.#dragging && this.#moved <= DRAG_THRESHOLD_PX;

    if (this.#dragging) {
      delete this.dataset.dragging;
      this.#savePosition();
      this.#stopTouchScrollBlock();
      try {
        this.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }

    if (openSocial && this.#pendingSocialTarget) {
      this.#openSocial(this.#pendingSocialTarget);
    }

    this.#pointerId = null;
    this.#armed = false;
    this.#dragging = false;
    this.#pendingSocialTarget = null;
    window.setTimeout(() => {
      this.#moved = 0;
    }, 0);
  };

}

class BymanayLiveChatLauncher extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.addEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const stack = this.closest('bymanay-live-chat-stack');
    if (stack instanceof BymanayLiveChatStack && stack.getDragDistance() > DRAG_THRESHOLD_PX) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    const panel = document.querySelector('bymanay-live-chat-panel');
    if (panel instanceof BymanayLiveChatPanel && stack instanceof HTMLElement) {
      panel.open(stack);
      stack.setAttribute('hidden', '');
    }
  };
}

if (!customElements.get('bymanay-live-chat-panel')) {
  customElements.define('bymanay-live-chat-panel', BymanayLiveChatPanel);
}
if (!customElements.get('bymanay-live-chat-stack')) {
  customElements.define('bymanay-live-chat-stack', BymanayLiveChatStack);
}
if (!customElements.get('bymanay-live-chat-launcher')) {
  customElements.define('bymanay-live-chat-launcher', BymanayLiveChatLauncher);
}
